import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { useSignalR } from "../../../hooks";
import { EditActionItemSectionDto, FetchError } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

type Variables = {
  actionItemGuid: string;
  fromPlanGuid: string;
  fromSectionGuid: string;
  toPlanGuid: string;
  toSectionGuid: string;
  sortOrder?: string;
};

const useEditActionItemSectionRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editActionItemSectionRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditActionItemSectionDto = {
      fromSectionGuid: variables.fromSectionGuid,
      toSectionGuid: variables.toSectionGuid,
      sortOrder: variables.sortOrder,
    };

    const response = await fetch(`${configuration.apiRootUrl}/action-items/${variables.actionItemGuid}/section`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editActionItemSectionRequest };
};

const useEditActionItemSection = () => {
  const { editActionItemSectionRequest } = useEditActionItemSectionRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editActionItemSectionRequest,
    onSuccess: async (_, variables) => {
      if (variables.fromPlanGuid) {
        await queryClient.refetchQueries({ queryKey: queryKeys.plans.getPlan(variables.fromPlanGuid) });
        signalR.sendRefetchPlan(variables.fromPlanGuid);
      }

      if (variables.toPlanGuid && variables.fromPlanGuid !== variables.toPlanGuid) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.plans.getPlan(variables.toPlanGuid) });
        signalR.sendRefetchPlan(variables.toPlanGuid);
      }

      await queryClient.refetchQueries({
        queryKey: queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid),
      });
      await queryClient.refetchQueries({
        queryKey: queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid),
      });

      signalR.sendRefetchActionItem(variables.actionItemGuid);
    },
  });
};

export { useEditActionItemSection };
